'use client';

/**
 * ContractSelectionCard Component
 *
 * A selectable card for multi-select contract type picking.
 * Used in the contract package selection step of partner invitations.
 *
 * Contract-Driven Partner Invitation System
 */

import LaunchIcon from '@mui/icons-material/Launch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { GlassSelect, type GlassSelectOption } from '../GlassSelect/GlassSelect';
import { GlassTooltip } from '../GlassTooltip';

type GlassMode = keyof typeof glassEffects;

/** Get theme-aware glass background styles */
const getGlassStyles = (mode: GlassMode) => {
  const glass = glassEffects[mode];
  return {
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    WebkitBackdropFilter: glass.backdropBlur,
  } as const;
};

// ============================================================================
// Types
// ============================================================================

export interface ContractSelectionOption {
  /** Unique identifier */
  value: string;
  /** Display name */
  label: string;
  /** Description text */
  description?: string;
  /** Icon key mapped to emoji (e.g. 'Handshake', 'AccountBalance') */
  icon?: string;
  /** Additional metadata */
  data?: Record<string, unknown>;
}

/**
 * Per-card template selection option.
 *
 * Issue #1540 / #1533 Phase 3 — When the wizard supplies a list of templates
 * for a contract type, the card renders a small dropdown letting the user
 * pick which version/variant of the template to bundle.
 */
export interface ContractTemplateChoice {
  /** Template id (Template.id from `unifiedTemplates.listForContractType`). */
  id: string;
  /** Display name (e.g. "NDA — v3" or "Custom NDA — Acme"). */
  name: string;
  /** Optional version number for display ("v" + version). */
  version?: number;
  /** Whether this row is the platform/tenant default for the contract type. */
  isDefaultForType?: boolean;
}

export interface ContractSelectionCardProps {
  /** Available options to select from */
  options: ContractSelectionOption[];
  /** Currently selected values */
  selectedValues: string[];
  /** Called when selection changes */
  onChange: (values: string[]) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Neon accent color for selected state */
  neonAccent?: NeonAccent;
  /** Number of columns in the grid */
  columns?: 2 | 3 | 4;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: boolean;
  /**
   * Optional link rendered above the grid that takes the user to the
   * source-of-truth management page for these options (e.g. a document
   * template manager). Opens in a new tab.
   */
  managementLink?: {
    href: string;
    /** i18n key for the link label */
    labelKey: string;
  };
  /**
   * URL pattern used by the per-card preview action to open the specific
   * template/resource in the management page. The literal `{id}` is
   * replaced with the template identifier discovered on the option data
   * (`defaultDocumentTemplateId` or falling back to the option value).
   * When omitted, per-card preview still works but only shows the dialog.
   */
  previewHrefPattern?: string;
  /**
   * Issue #1382 — Optional callback to fetch an inline renderable preview
   * for the selected template when the preview dialog opens. When provided,
   * the dialog renders an `<iframe>` / `<img>` of the returned URL. When
   * omitted (or when the template has no id), the dialog falls back to the
   * external deep-link CTA.
   *
   * Must be tenant-scoped on the server — the design system passes through
   * whatever the caller returns without additional isolation.
   */
  onFetchPreview?: (templateId: string) => Promise<TemplatePreviewData>;

  // ==========================================================================
  // Per-card template+version picker (Issue #1540 / #1533 Phase 3)
  //
  // When `templatesPerOption` is supplied AND a card is selected, the card
  // renders an inline dropdown of templates for that contract type. The
  // wizard owns the selection state and pushes updates through
  // `onTemplateSelectChange`. These props are optional and additive — when
  // omitted, the card behaves exactly as before (single-template path
  // driven by `option.data.defaultDocumentTemplateId`).
  // ==========================================================================
  /**
   * Map keyed by `option.value` (contract type definition id) → list of
   * templates to show in the per-card dropdown. The list should already be
   * sorted with the default-for-type row first; the component preserves
   * order.
   */
  templatesPerOption?: Record<string, ContractTemplateChoice[]>;
  /** Currently chosen templateId per option, keyed by `option.value`. */
  selectedTemplateByOption?: Record<string, string>;
  /** Called when the user changes the per-card template dropdown. */
  onTemplateSelectChange?: (optionValue: string, templateId: string) => void;
  /** Optional per-option loading flag for the dropdown. */
  templatesLoadingByOption?: Record<string, boolean>;
  /**
   * Optional inline action rendered next to the dropdown — typically
   * "Upload custom template" routing to the templates manager page with
   * `contractType` preselected.
   */
  uploadCustomLink?: {
    /** Pattern with `{contractType}` placeholder; replaced per option. */
    hrefPattern: string;
    /** i18n key for the link label. */
    labelKey: string;
    /** Resolves the contractType slug used in the href from the option. */
    contractTypeFromOption?: (option: ContractSelectionOption) => string | undefined;
  };
}

export interface TemplatePreviewData {
  previewUrl: string;
  contentType: 'image/png' | 'image/jpeg' | 'application/pdf' | 'text/html' | null;
  hasPreview: boolean;
}

// ============================================================================
// Icon Mapping (Material Design icons used as contract type icons)
// ============================================================================

const ICON_MAP: Record<string, string> = {
  Handshake: '\u{1F91D}',
  AccountBalance: '\u{1F3E6}',
  Warehouse: '\u{1F3ED}',
  Hub: '\u{1F310}',
  Security: '\u{1F512}',
  PrivacyTip: '\u{1F6E1}️',
  Gavel: '⚖️',
  Verified: '✅',
};

const getIconEmoji = (icon?: string): string => (icon ? ICON_MAP[icon] || '\u{1F4C4}' : '\u{1F4C4}');

/**
 * Resolve the template id to preview for a card.
 *
 * Prefers the per-card picker selection (`selectedTemplateByOption`) — that
 * matches what the user actually picked in the dropdown. Falls back to the
 * legacy `defaultDocumentTemplateId` on `option.data` for callers that
 * pre-date the per-card picker (Issue #1540 / #1545). With #1545 the
 * platform stopped emitting `defaultDocumentTemplateId` on
 * `partnerInvitations.listContractTypes`, so the picker path is the
 * primary source today.
 */
const getTemplateId = (
  option: ContractSelectionOption,
  selectedTemplateByOption?: Record<string, string>,
): string | undefined => {
  const picked = selectedTemplateByOption?.[option.value];
  if (typeof picked === 'string' && picked) return picked;
  const raw = option.data?.['defaultDocumentTemplateId'];
  return typeof raw === 'string' && raw ? raw : undefined;
};

const ExternalLink: React.FC<{ href: string; label: string; fontSize?: string }> = ({
  href,
  label,
  fontSize = '0.8125rem',
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    underline="hover"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize,
      fontWeight: 500,
      color: 'primary.main',
    }}
  >
    {label}
    <LaunchIcon sx={{ fontSize: '1em' }} />
  </Link>
);

const PreviewFrame: React.FC<{ data: TemplatePreviewData; altLabel: string }> = ({ data, altLabel }) => {
  const frameSx = {
    width: '100%',
    height: 420,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.default',
  } as const;

  if (data.contentType === 'image/png' || data.contentType === 'image/jpeg') {
    return (
      <Box
        component="img"
        src={data.previewUrl}
        alt={altLabel}
        sx={{
          ...frameSx,
          height: 'auto',
          maxHeight: 520,
          objectFit: 'contain',
        }}
      />
    );
  }
  return (
    <Box
      component="iframe"
      src={data.previewUrl}
      title={altLabel}
      sx={frameSx}
      // Sandbox the embed to minimize XSS surface for HTML previews while
      // still allowing DocuSeal-hosted PDF viewers to render.
      sandbox="allow-same-origin allow-scripts allow-popups"
    />
  );
};

interface PreviewBodyProps {
  loading: boolean;
  error: string | null;
  data: TemplatePreviewData | null;
  hasFetcher: boolean;
  templateId: string;
  t: (key: string, vars?: Record<string, unknown>) => string;
}

const PreviewBody: React.FC<PreviewBodyProps> = ({ loading, error, data, hasFetcher, templateId, t }) => {
  if (!hasFetcher) {
    // No fetcher wired yet — fall back to showing the template id so the
    // deep-link CTA below still lets the user navigate to the details page.
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {t('contractPackage.templateId')}: {templateId}
      </Typography>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('contractPackage.previewLoading')}
        </Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }}>
        {t('contractPackage.previewError')}
      </Typography>
    );
  }
  if (!data || !data.hasPreview || !data.previewUrl) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('contractPackage.previewUnavailable')}
      </Typography>
    );
  }
  return <PreviewFrame data={data} altLabel={t('contractPackage.preview')} />;
};

// ============================================================================
// Component
// ============================================================================

export const ContractSelectionCard: React.FC<ContractSelectionCardProps> = ({
  options,
  selectedValues,
  onChange,
  disabled = false,
  columns = 3,
  loading = false,
  managementLink,
  previewHrefPattern,
  onFetchPreview,
  templatesPerOption,
  selectedTemplateByOption,
  onTemplateSelectChange,
  templatesLoadingByOption,
  uploadCustomLink,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const [previewOption, setPreviewOption] = useState<ContractSelectionOption | null>(null);
  const [previewData, setPreviewData] = useState<TemplatePreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handleToggle = useCallback(
    (value: string) => {
      if (disabled || loading) return;
      const isSelected = selectedValues.includes(value);
      if (isSelected) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    },
    [selectedValues, onChange, disabled, loading],
  );

  const handleOpenPreview = useCallback((event: React.MouseEvent, option: ContractSelectionOption) => {
    event.stopPropagation();
    setPreviewOption(option);
    setPreviewData(null);
    setPreviewError(null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOption(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }, []);

  const previewTemplateId = previewOption ? getTemplateId(previewOption, selectedTemplateByOption) : undefined;
  const previewHref =
    previewHrefPattern && previewTemplateId
      ? previewHrefPattern.replace('{id}', encodeURIComponent(previewTemplateId))
      : managementLink?.href;

  // Fetch the renderable preview URL when the dialog opens on a template
  // that has a backing id. Effect handles race conditions by ignoring stale
  // responses after the dialog closes or the selected option changes.
  useEffect(() => {
    if (!previewOption || !previewTemplateId || !onFetchPreview) {
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    onFetchPreview(previewTemplateId)
      .then((data) => {
        if (cancelled) return;
        setPreviewData(data);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        setPreviewError(message);
      })
      .finally(() => {
        if (cancelled) return;
        setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [previewOption, previewTemplateId, onFetchPreview]);

  /**
   * Render the per-card template+version picker (Issue #1540 / #1533 Phase 3).
   *
   * Wired only when the caller supplies `templatesPerOption`. The picker
   * lives inside the card body but stops click propagation so interacting
   * with the dropdown doesn't toggle the card off.
   *
   * Template list semantics (per `unifiedTemplates.listForContractType`):
   *   - Already filtered to ACTIVE rows.
   *   - Default-for-type row is sorted first (preselected by the wizard).
   *   - When multiple ACTIVE rows exist, latest `updatedAt` is the implicit
   *     default for the version dropdown.
   */
  const renderTemplatePicker = (option: ContractSelectionOption) => {
    const choices = templatesPerOption?.[option.value] ?? [];
    const isLoading = templatesLoadingByOption?.[option.value] === true;
    const selectedTemplateId = selectedTemplateByOption?.[option.value] ?? '';

    const selectOptions: GlassSelectOption[] = choices.map((c) => {
      const versionSuffix =
        typeof c.version === 'number' ? ` ${t('partners.invite.step3.templateVersionPrefix')}${c.version}` : '';
      const defaultSuffix = c.isDefaultForType ? ` (${t('partners.invite.step3.templateDefaultBadge')})` : '';
      return {
        value: c.id,
        label: `${c.name}${versionSuffix}${defaultSuffix}`,
      };
    });

    const contractTypeSlug = uploadCustomLink?.contractTypeFromOption?.(option);
    const uploadHref =
      uploadCustomLink && contractTypeSlug
        ? uploadCustomLink.hrefPattern.replace('{contractType}', encodeURIComponent(contractTypeSlug))
        : undefined;

    return (
      <Box
        // The dropdown is a child of the click-toggle card; swallow click+key
        // events so opening it doesn't deselect the contract type.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('partners.invite.step3.templateLoading')}
            </Typography>
          </Box>
        ) : selectOptions.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('partners.invite.step3.templateNoneAvailable')}
          </Typography>
        ) : (
          <GlassSelect
            label={t('partners.invite.step3.templateLabel')}
            options={selectOptions}
            value={selectedTemplateId}
            disabled={disabled}
            size="small"
            fullWidth
            onChange={(e) => {
              const next = e.target.value as string;
              onTemplateSelectChange?.(option.value, next);
            }}
          />
        )}

        {uploadHref && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ExternalLink href={uploadHref} label={t(uploadCustomLink!.labelKey)} fontSize="0.75rem" />
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 12 / columns }}>
            <Box
              sx={{
                ...getGlassStyles(theme.palette.mode as GlassMode),
                borderRadius: 2,
                p: 2.5,
                height: 120,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      {managementLink && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <ExternalLink href={managementLink.href} label={t(managementLink.labelKey)} />
        </Box>
      )}

      <Grid container spacing={2}>
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const iconEmoji = getIconEmoji(option.icon);

          return (
            <Grid key={option.value} size={{ xs: 12, sm: 6, md: 12 / columns }}>
              <ButtonBase
                component="div"
                onClick={() => handleToggle(option.value)}
                disabled={disabled}
                role="checkbox"
                aria-checked={isSelected}
                aria-label={option.label}
                tabIndex={disabled ? -1 : 0}
                // Issue #1562 / #130: switching the outer card from
                // `<Box onClick>` to `<ButtonBase>` so headless Chromium /
                // React 19 dispatches the click cleanly without needing
                // fiber-walk fallbacks in cross-portal POMs.
                //
                // `component="div"` is REQUIRED here (PR #1564 review by
                // Copilot): the card body contains other interactive
                // controls — `<IconButton>` (preview), `<Checkbox>` (select
                // indicator), and a `<GlassSelect>` template-picker that
                // mounts when the card is selected. Letting ButtonBase
                // render as its default `<button>` would create
                // button-in-button, which is invalid HTML and breaks
                // keyboard / screen-reader behavior.
                //
                // ButtonBase still attaches a real DOM click listener and
                // its own keyboard handler (Space + Enter → onClick) when
                // `component` overrides the root element, so the headless
                // click reliability we needed is preserved. We add
                // `tabIndex` explicitly because ButtonBase only sets it
                // automatically when rendering as `<button>`.
                //
                // The inner IconButton already calls `event.stopPropagation()`
                // in `handleOpenPreview` so the preview button doesn't
                // toggle the card.
                sx={{
                  ...getGlassStyles(theme.palette.mode as GlassMode),
                  borderRadius: 2,
                  p: 2.5,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  border: '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                  boxShadow: isSelected ? `0 0 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': disabled
                    ? {}
                    : {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  gap: 1,
                  minHeight: 120,
                  position: 'relative',
                  width: '100%',
                }}
              >
                {/* Preview + Checkbox in top-right corner */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <GlassTooltip title={t('contractPackage.preview')}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenPreview(e, option)}
                      disabled={disabled}
                      aria-label={t('contractPackage.preview')}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </GlassTooltip>
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                    tabIndex={-1}
                  />
                </Box>

                {/* Icon + Title row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 8 }}>
                  <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                    {iconEmoji}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color={isSelected ? 'primary.main' : 'text.primary'}
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>

                {/* Description */}
                {option.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.5,
                    }}
                  >
                    {option.description}
                  </Typography>
                )}

                {/*
                  Per-card template+version picker (Issue #1540 / #1533 Phase 3).
                  Renders only when:
                    1) the card is selected,
                    2) `templatesPerOption` is supplied by the caller (additive),
                    3) we have an entry for this option's value (it may be empty).
                  Stops click-propagation so picking a template doesn't toggle
                  the card off.
                */}
                {isSelected && templatesPerOption !== undefined && renderTemplatePicker(option)}
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>

      {previewOption && (
        <Dialog
          open
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                ...getGlassStyles(theme.palette.mode as GlassMode),
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              },
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
            <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
              {getIconEmoji(previewOption.icon)}
            </Typography>
            <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
              {previewOption.label}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 360 }}>
            {previewOption.description ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {previewOption.description}
              </Typography>
            ) : null}
            {previewTemplateId ? (
              <PreviewBody
                loading={previewLoading}
                error={previewError}
                data={previewData}
                hasFetcher={Boolean(onFetchPreview)}
                templateId={previewTemplateId}
                t={t}
              />
            ) : (
              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                {t('contractPackage.noTemplateAssigned')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            {previewHref && (
              <ExternalLink
                href={previewHref}
                label={
                  previewTemplateId ? t('contractPackage.openInControlCenter') : t('contractPackage.manageTemplates')
                }
                fontSize="0.875rem"
              />
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
